/**
 * Custom Lexical node for blurb embed elements
*/

import { yupResolver } from '@hookform/resolvers/yup';
import { $createParagraphNode, $getSelection, $isRangeSelection, type ElementFormatType, type LexicalNode, type NodeKey } from 'lexical';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import * as yup from "yup";

import { SystemDialogContent } from '@cms/components/data/Dialog';
import { TStoryContentElementTypeCustomEmbed, TStoryContentElementTypeCustomEmbedChart } from '@dl/declarations';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ControlledDropdown } from '../../data/Dropdown';
import { ControlledSystemSwitch } from '../../data/Switch';
import { ControlledSystemTextInput } from '../../data/TextInput';
import { CustomEmbedNode, SerializeEmbedNode } from './CustomEmbedNode';
import { CustomEmbedPluginActions, CustomEmbedPluginContainer } from './CustomEmbedPluginContainer';

type TBaseType = TStoryContentElementTypeCustomEmbed;
type TExtendedType = TStoryContentElementTypeCustomEmbedChart;


// Max and Min dates, today and 1 year ago
const maxDate = new Date().toISOString().split('T')[0]
const minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0]

const CHARTS = [
    { name: 'Hacks', contentService: 'defi-hacks-chart-api', params: [] },
    {
        name: 'Raises',
        contentService: 'defillama-raises-chart-api',
        params: [
            {
                name: 'category',
                displayName: 'Category',
                type: 'text',
            },
        ],
    },
    {
        name: 'DeFi TVL',
        contentService: 'defillama-defi-tvl-chart-api',
        params: [
            {
                name: 'chain',
                displayName: 'DefiLlama chain slug (optional)',
                type: 'text',
            },
        ],
    },
    {
        name: 'Chain TVL',
        contentService: 'defillama-chain-tvl-chart-api',
        params: [
            {
                name: 'chain',
                displayName: 'DefiLlama chain slug',
                type: 'text',
            },
        ],
    },
    {
        name: 'Coin Price',
        contentService: 'defillama-coins-price-chart-api',
        params: [
            {
                name: 'coingeckoId',
                displayName: 'CoinGecko ID (or chain + address)',
                type: 'text',
            },
            {
                name: 'chain',
                displayName: 'Chain',
                type: 'text',
            },
            {
                name: 'address',
                displayName: 'Address (empty for native coin)',
                type: 'text',
            },
            {
                name: 'start',
                displayName: 'Start date:',
                type: 'date',
                min: minDate,
                max: maxDate,
            },
            {
                name: 'end',
                displayName: 'End date: (empty for start - now)',
                type: 'date',
                min: minDate,
                max: maxDate,
            },
            {
                name: 'lastXDays',
                displayName: "Last X Days (can't use with start/end)",
                type: 'number',
            },
            {
                name: 'period',
                displayName: 'Period: m | h | d | w (optional) (default 1d)',
                type: 'text',
            },
        ],
    },
    {
        name: 'Coin Price (CoinGecko)',
        contentService: 'coingecko-coins-market-chart-api',
        params: [
            {
                name: 'coingeckoId',
                displayName: 'Coingecko ID',
                type: 'text',
            },
            {
                name: 'lastXDays',
                displayName: 'last X Days: 1 | 14 | 30 | Limited to 365 days (exclusive with from/to params)',
                type: 'number',
                min: 1,
                max: 365,
                defaultValue: 365,
            },
            {
                name: 'from',
                displayName: 'From: (exclusive with lastXDays)',
                type: 'date',
                min: minDate,
                max: maxDate,
            },
            {
                name: 'to',
                displayName: 'To: (exclusive with lastXDays)',
                type: 'date',
                min: minDate,
                max: maxDate,
            },
        ],
    },
    { name: 'DEXs Volume', contentService: 'defillama-dexs-volume-chart-api', params: [] },
    {
        name: 'Protocol TVL',
        contentService: 'defillama-protocol-tvl-chart-api',
        params: [
            {
                name: 'defiLlamaId',
                displayName: 'DefiLlama protocol slug',
                type: 'text',
            },
            {
                name: 'chain',
                displayName: 'DefiLlama chain slug (optional)',
                type: 'text',
            },
            {
                name: 'showHallmarks',
                displayName: 'Show events (optional)',
                type: 'checkbox',
            },
        ],
    },
    {
        name: 'Top Protocol TVL',
        contentService: 'defillama-top-protocols-tvl-chart-api',
        params: [
            {
                name: 'size',
                displayName: 'Size',
                type: 'number',
            },
        ],
    },
    { name: 'Liquid Staking Derivatives', contentService: 'defillama-lsd-chart-api', params: [] },
    { name: 'Stablecoins Chains Pie', contentService: 'defillama-stablecoins-chains-pie-chart-api', params: [] },
    { name: 'Stablecoins Pie', contentService: 'defillama-stablecoins-pie-chart-api', params: [] },
    {
        name: 'Stablecoins Tokens Market Cap',
        contentService: 'defillama-stablecoins-market-cap-chart-api',
        params: [
            {
                name: 'chain',
                displayName: 'Chain (DefiLlama format) (optional)',
                type: 'text',
            },
        ],
    },
    {
        name: 'NFT Marketplaces Trade',
        contentService: 'defillama-nft-marketplace-trade-api',
        params: [
            {
                name: 'lastXDays',
                displayName: 'Last X Days (optional)',
                type: 'number',
            },
        ],
    },
    {
        name: 'NFT Marketplaces Volume',
        contentService: 'defillama-nft-marketplace-volume-api',
        params: [
            {
                name: 'lastXDays',
                displayName: 'Last X Days (optional)',
                type: 'number',
            },
        ],
    }
]
interface EmbedChartProps {
    config: TBaseType
}

export const EmbedChart: React.FC<EmbedChartProps> = ({ config }) => {
    const usedConfig = config as TExtendedType;
    const { caption, chartSource, contentService } = usedConfig.embed.config

    const chartType = contentService ? CHARTS.find((c) => c.contentService === contentService)?.name : 'Unknown';

    return (
        <div className="border w-full p-4 rounded">
            <p className='text-red-500 font-bold'>Internal note: Please, switch to Preview mode to view the chart.</p>
            <p className=''>Source: <span className='uppercase font-bold'>{chartSource}</span></p>
            <p className=''>Type: <span className='font-bold'>{chartType}</span></p>
            <p className=''>Caption: <span className='font-bold'>{caption}</span></p>
        </div>
    )
}

const ApiChart: React.FC<EmbedChartProps> = ({ config }) => {
    const [paramsFields, setParamsFields] = useState<Array<typeof CHARTS[number]['params'][number]>>([])

    const { watch, setValue } = useFormContext();

    const [
        contentService,
        // component
    ] = watch([
        "contentService",
        // "component"
    ]);

    useEffect(() => {
        if (contentService) {
            const chart = CHARTS.find((c) => c.contentService === contentService)

            if (chart?.params) {
                setParamsFields(chart.params)
            }
            else {
                setParamsFields([])
            }
        }
        else {
            setParamsFields([])
        }
    }, [contentService]);

    useEffect(() => {
        const usedConfig = config?.embed?.config
        paramsFields.forEach(({ name, defaultValue }) => {
            setValue(
                name,
                usedConfig?.[name as keyof typeof usedConfig]
                || usedConfig?.[`config_${name}` as keyof typeof usedConfig]
                || defaultValue
                || ''
            )
        })
    }, [config, paramsFields]);

    return (
        <>
            <ControlledDropdown
                name="contentService"
                options={CHARTS.map((c) => ({
                    _id: c.contentService,
                    label: c.name,
                    slug: c.contentService,
                    groupKey: 'Options'
                }))}
                valueKey='slug'
                inputSize='full'
            />

            <ControlledDropdown
                name="component"
                options={[
                    { _id: '1', label: 'Area Chart', slug: 'area', groupKey: 'Options' },
                    { _id: '2', label: 'Bar Chart', slug: 'bar', groupKey: 'Options' },
                    { _id: '3', label: 'Pie Chart', slug: 'pie', groupKey: 'Options' },
                ]}
                valueKey='slug'
                inputSize='full'
                allowSingleDeselect={false}
                searchOptions={{
                    allow: false,
                }}
            />

            <ControlledSystemTextInput
                name="caption"
                inputSize='full'
                placeholder="Chart Caption"
            />

            {/* {component === 'area' && contentService === 'coingecko-coins-market-chart-api' && (
                <ControlledSystemTextInput
                    name='denominators'
                    inputSize='full'
                    placeholder="Denominators: (optional) (coingecko IDs, comma separated)"
                />
            )} */}

            <div className='flex items-start gap-2'>
                <ControlledSystemSwitch
                    name='stacked'
                    label='Stacked'
                />

                <ControlledDropdown
                    name='timeframe'
                    options={[
                        { _id: '1', label: 'Daily', slug: 'D', groupKey: 'Options' },
                        { _id: '2', label: 'Weekly', slug: 'W', groupKey: 'Options' },
                        { _id: '3', label: 'Monthly', slug: 'M', groupKey: 'Options' },
                    ]}
                    valueKey='slug'
                />
            </div>


            {paramsFields.map(({ name, displayName, type, ...inputProps }) => (
                <ControlledSystemTextInput
                    // key={name}
                    // // name={`config_${name}`}
                    // name={name}
                    // inputSize='full'
                    // label={displayName}
                    // type={type || 'text'}
                    // {...inputProps}
                    key={name}
                    {...{
                        name: name,
                        inputSize: 'full',
                        type: type || 'text',
                        ...(type === 'text' ? { label: displayName } : { description: displayName }),
                        ...inputProps,
                    }}
                />
            ))}
        </>
    )
}

const IFrameChart: React.FC<EmbedChartProps> = () => {
    return (
        <>
            <ControlledSystemTextInput
                name="title"
                inputSize='full'
                placeholder="Title"
            />

            <ControlledDropdown
                name="titlePosition"
                options={[
                    { _id: '1', slug: "top", label: "Top", groupKey: "Options" },
                    { _id: '2', slug: "bottom", label: "Bottom", groupKey: "Options" },
                ]}
                valueKey='slug'
            />

            <ControlledSystemTextInput
                name="html"
                inputSize='full'
                placeholder="HTML"
                multiline
                rows={5}
            />

            <hr className="form-row col-full" />

            <p className="form-row col-full" style={{ color: 'red', fontSize: '16px' }}>
                Important: iframes are external code and are not auto sized, please check how they look on different screen
                widths before publish
            </p>

            <div className='grid grid-cols-2 gap-2'>
                <ControlledSystemTextInput
                    name="height"
                    description='Height for screens with mobile screen size'
                    defaultValue="340"
                />

                <ControlledSystemTextInput
                    name='heightSm'
                    description='Height for screens with tablet screen size'
                    defaultValue="340"
                />

                <ControlledSystemTextInput
                    name='heightMd'
                    description='Height for screens with desktop small screen size'
                    defaultValue="340"
                />

                <ControlledSystemTextInput
                    name="heightLg"
                    description='Height for screens with desktop medium screen size'
                    defaultValue="340"
                />
            </div>
        </>
    )
}

export const EditEmbedChart: React.FC<
    EmbedChartProps & {
        handleSubmit?: (payload: TExtendedType) => void;
    }
> = ({ config, handleSubmit }) => {
    const usedConfig = config as TExtendedType;

    const [editor] = useLexicalComposerContext();

    const methods = useForm<
        // TExtendedType['embed']['config']
        any
    >({
        resolver: yupResolver(yup.object().shape({
            chartSource: yup.string().optional().oneOf(['iframe', 'api']).label("Chart Source"),

            // API fields
            contentService: yup.string().optional().label("Content Service"),
            component: yup.string().oneOf(['bar', 'pie', 'area']).optional().label("Chart Type"),
            caption: yup.string().optional().label("Caption"),
            denominators: yup.string().optional().label("Denominators (optional)"),
            stacked: yup.boolean().optional().label("Stacked"),
            timeframe: yup.string().oneOf(['D', 'W', 'M']).optional().label("Timeframe"),

            // IFrame fields
            title: yup.string().optional().label("Title"),
            titlePosition: yup.string().oneOf(['top', 'bottom']).optional().label("Title Position"),
            html: yup.string().optional().label("HTML"),
            height: yup.string().optional().label("Height"),
            heightSm: yup.string().optional().label("Height for small screens"),
            heightMd: yup.string().optional().label("Height for medium screens"),
            heightLg: yup.string().optional().label("Height for large screens"),
        })),
        defaultValues: {
            chartSource: usedConfig.embed.config.chartSource || 'api',

            // API fields
            contentService: usedConfig.embed.config.contentService || '',
            component: usedConfig.embed.config.component || 'area',
            caption: usedConfig.embed.config.caption || '',
            denominators: usedConfig.embed.config.denominators || '',
            stacked: usedConfig.embed.config.stacked || false,
            timeframe: usedConfig.embed.config.timeframe || 'M',

            // IFrame fields
            html: usedConfig.embed.config.html || '',
            title: usedConfig.embed.config.title || '',
            titlePosition: usedConfig.embed.config.titlePosition || 'top',
            height: usedConfig.embed.config.height || '340',
            heightSm: usedConfig.embed.config.heightSm || '340',
            heightMd: usedConfig.embed.config.heightMd || '340',
            heightLg: usedConfig.embed.config.heightLg || '340',
        }
    })

    const { handleSubmit: handleFormSubmit, watch } = methods;

    const chartSource = watch("chartSource");

    const handleSubmitEdits = handleFormSubmit((data) => {
        editor.update(() => {
            const payload: TExtendedType = {
                ...usedConfig,
                embed: {
                    ...usedConfig.embed,
                    config: {
                        ...usedConfig.embed.config,
                        ...data
                    }
                }
            }

            if (handleSubmit) {
                handleSubmit(payload)
            }
            else {
                const selection = $getSelection();

                if ($isRangeSelection(selection) && selection.isCollapsed()) {
                    const node = $createChartNode(payload as TStoryContentElementTypeCustomEmbed);
                    selection.insertNodes([node, $createParagraphNode()]);
                }
            }
        })
    })

    return (
        <FormProvider {...methods} >
            <CustomEmbedPluginContainer
                embedTitle='Embed/Embed Chart'
                embedDescription={(
                    <>
                        <b>Choose a live chart</b> you need for the article, by an <b>API</b> or an <b>Iframe</b>.
                    </>
                )}
                instructionsScreenshotUrl='/images/embeds/chart.png'
                embedActionComponent={(
                    <FormProvider {...methods}>
                        <form className="flex flex-col gap-4">
                            <ControlledDropdown
                                name='chartSource'
                                options={[
                                    { _id: '1', label: 'IFRAME', slug: 'iframe', groupKey: 'Options' },
                                    { _id: '2', label: 'API', slug: 'api', groupKey: 'Options' },
                                ]}
                                valueKey='slug'
                                inputSize='full'
                                allowSingleDeselect={false}
                            />

                            {chartSource === 'api' && <ApiChart config={config} />}
                            {chartSource === 'iframe' && <IFrameChart config={config} />}

                            <CustomEmbedPluginActions
                                handleSubmit={handleSubmitEdits}
                            />
                        </form>
                    </FormProvider>
                )}
            />
        </FormProvider>
    );
}

export const EditEmbedChartDialog: React.FC<
    EmbedChartProps
> = ({ config }) => {
    return (
        <SystemDialogContent
            dialogProps={{
                title: 'Embed/Chart',
            }}
            content={{
                component: (
                    <EditEmbedChart config={config} />
                ),
            }}
            actionsProps={{
                hide: true
            }}
        />
    )
}

export class CustomChartNode extends CustomEmbedNode {
    __config: TBaseType;

    constructor(
        config: TBaseType,
        format?: ElementFormatType,
        key?: NodeKey,
    ) {
        super(
            "Embed Chart",
            <EmbedChart config={config} />,
            <EditEmbedChart config={config} handleSubmit={(payload) => { this.handleSubmit(payload) }} />,
            config,
            format,
            key
        );
        this.__config = config;
    }

    handleSubmit(config: TExtendedType) {
        this.setConfig(config as TBaseType);
    }

    static getType(): string {
        return "chart"
    }

    static clone(node: CustomChartNode): CustomChartNode {
        return new CustomChartNode(node.__config, node.__format, node.__key);
    }

    static importJSON(serializedNode: SerializeEmbedNode): CustomChartNode {
        return $createChartNode(
            serializedNode.config,
        ).updateFromJSON(
            serializedNode,
        );
    }
}

export function $createChartNode(
    config: TBaseType,
): CustomChartNode {
    return new CustomChartNode(config);
}

export function $isChartNode(
    node: CustomChartNode | LexicalNode | null | undefined,
): node is CustomChartNode {
    return node instanceof CustomChartNode;
}
